import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, ViewChild } from '@angular/core';
import { AcreditacionEmpresaFormInput, AcreditacionEmpresaFormOutput } from "./acreditacion-empresa-form.interfaces"
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CheckItem, CheckListFormComponent, CheckListFormInput } from '@rpe/registro-personas-empresas-lib';
import { Subscription } from 'rxjs';
import { FileUploaderImages } from 'projects/registro-personas-empresas/src/app/shared/file-uploader/file-uploader.interfaces';
import { EntityEtapasEmpresa } from '../../../../../entity-etapas-empresa/entity-etapas-empresa.model';

@Component({
  selector: 'rpe-acreditacion-empresa-form',
  templateUrl: './acreditacion-empresa-form.component.html',
  styleUrls: ['./acreditacion-empresa-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AcreditacionEmpresaFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() acreditacionEmpresaFormInput: AcreditacionEmpresaFormInput
  @Output() acreditacionEmpresaFormOutput: EventEmitter<AcreditacionEmpresaFormOutput> = new EventEmitter<AcreditacionEmpresaFormOutput>()

  @ViewChild("checkList", { static: true }) public checklistComponent: CheckListFormComponent

  etapasCheckValid: boolean = false
  maxFilesInvalid: boolean = false

  subscriptions: Subscription[] = []

  especiesCertificables = this.toEspeciesCertificables()
  etapas = this.toEtapas()

  checkboxListInput: CheckListFormInput = {
    action: null,
    elementosCheck: [],
    addOther: null
  }

  especiesCertificable: FormControl = new FormControl([], [Validators.required])
  etapa: FormControl = new FormControl([], [Validators.required])
  files: FormControl = new FormControl([], [Validators.required])

  form: FormGroup = new FormGroup({
    especiesCertificable: this.especiesCertificable,
    etapa: this.etapa,
    files: this.files,
  })

  get fileValid(): boolean {
    return this.files.valid && this.files?.value.length > 0 && !this.maxFilesInvalid
  }

  get etapasValid(): boolean {
    return this.etapa.valid && this.etapa?.value?.length > 0
  }

  get especiesValid(): boolean {
    return this.especiesCertificable.valid && this.especiesCertificable?.value?.length > 0
  }

  ngOnInit(): void {
    this.checklistComponent.form.markAllAsTouched()

    const s1 = this.form.statusChanges.subscribe((f) => {
      this.acreditacionEmpresaFormOutput.emit({
        etapas_id: this.etapa.value?.map((etapa: CheckItem) => etapa.id),
        especies_certificable_id: this.especiesCertificable.value,
        isValid: f === 'VALID' && this.etapasValid && this.fileValid && this.especiesValid,
        files: this.files.value?.map((file: FileUploaderImages) => file.target),
      })
    })

    const s2 = this.form.valueChanges.subscribe((_) => {
      this.acreditacionEmpresaFormOutput.emit({
        etapas_id: this.etapa.value?.map((etapa: CheckItem) => etapa.id),
        especies_certificable_id: this.especiesCertificable.value,
        isValid: this.form.valid && this.etapasValid && this.fileValid && this.especiesValid,
        files: this.files.value,
      })
    })

    this.subscriptions.push(s1, s2)
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe())
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.especiesCertificables = this.toEspeciesCertificables()
    this.etapas = this.toEtapas()
    this.checkboxListInput = this.etapasToCheckboxListInput()

    if (changes['acreditacionEmpresaFormInput']) {
      this.acreditacionEmpresaFormInput = changes['acreditacionEmpresaFormInput'].currentValue
      this.setItems()
    }
  }

  setItems(): void {
    const especies = this.acreditacionEmpresaFormInput?.especies_certificable_id || [];
    const seleccionados = this.especiesCertificables.filter((especie) => especies.includes(especie.value)).map((e) => e.value);
    this.especiesCertificable.setValue(seleccionados);
  }

  onEspeciesChange($event: { value: number, label: string }[]): void {
    if ($event.length > 0) {
      const especiesIds = $event.map((especie) => especie.value)
      this.especiesCertificable.setValue(especiesIds)
    } else {
      this.especiesCertificable.setValue(null)
    }
  }

  toEspeciesCertificables(): Array<{ value: number, label: string }> {
    const especies = this.acreditacionEmpresaFormInput?.especiesCertificables.map((result) => {
      return {
        value: result.id_cultivar,
        label: result.nombre_comun + ' - ' + result.nombre_cientifico
      }
    }) || []

    return especies
  }

  toEtapas(): Array<{ value: number, label: string }> {
    const etapas = this.acreditacionEmpresaFormInput?.etapas.map((result) => {
      return {
        value: result.id,
        label: result.nombre
      }
    }) || []

    return etapas
  }

  etapasToCheckboxListInput(): CheckListFormInput {
    return {
      action: null,
      elementosCheck: this.etapas.map((etapa) => {
        return {
          id: etapa.value,
          nombre: etapa.label,
          tipo_id: null,
          selected: this.acreditacionEmpresaFormInput.etapas_id?.includes(etapa.value)
        }
      }),
      addOther: null
    }
  }

  onEtapasFormStatusChange($event: string): void {
    this.etapasCheckValid = ($event === 'VALID')
  }

  onEtapasFormChange($event: CheckItem[]): void {
    if ($event.length > 0) {
      this.etapa.setValue($event)
    } else {
      this.etapa.setValue(null)
    }
  }

  onFileChange($event: FileUploaderImages[]): void {
    if ($event.length > 0) {
      this.files.setValue($event)
    } else {
      this.files.setValue(null)
    }
  }

  onMaxFilesChange($event: boolean): void {
    this.maxFilesInvalid = $event
  }

  getEtapaById(id: number): EntityEtapasEmpresa {
    return this.acreditacionEmpresaFormInput.etapas.find((etapa) => +etapa.id === +id)
  }

  compareFunction(item: any, selected: any) {
    return item && selected && item.value === selected.value
  }
}
